import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { render as enzymeRender } from 'enzyme';
import {
  TPAComponentsProvider,
  TPAComponentsConfig,
} from '../TPAComponentsConfig';
import { WeeklyDateNavigation, WeeklyDateNavigationProps } from '.';
import { DATA_HOOKS } from './constants';
import { weeklyDateNavigationDriverFactory } from './WeeklyDateNavigation.driver';
import { startOfDay, endOfDay } from 'date-fns';

describe('WeeklyDateNavigation', () => {
  const createDriver = createRendererWithUniDriver(
    weeklyDateNavigationDriverFactory,
  );

  const render = (
    props: Partial<WeeklyDateNavigationProps> = {},
    contextProps: TPAComponentsConfig = {},
  ) => {
    const dataHook = 'compDataHook';
    const value = {
      startOfWeek: startOfDay(new Date('2021-04-11')),
      endOfWeek: endOfDay(new Date('2021-04-11')),
    };
    const onChange = () => {};
    const compProps = {
      'data-hook': dataHook,
      onChange,
      value,
      ...props,
    } as WeeklyDateNavigationProps;
    return createDriver(
      <TPAComponentsProvider value={contextProps}>
        <WeeklyDateNavigation {...compProps} />
      </TPAComponentsProvider>,
    );
  };

  it('should render', async () => {
    const { driver: component } = render();

    expect(await component.exists()).toBe(true);
  });

  it('should call onChange with previous week when clicking on the prev arrow', async () => {
    const onChange = jest.fn();
    const value = {
      startOfWeek: startOfDay(new Date('2021-07-12')),
      endOfWeek: endOfDay(new Date('2021-07-18')),
    };
    const expectedRange = {
      startOfWeek: startOfDay(new Date('2021-07-05')),
      endOfWeek: endOfDay(new Date('2021-07-11')),
    };
    const { driver: component } = render({ value, onChange });

    await component.clickOnPrevWeekButton();

    expect(onChange).toHaveBeenLastCalledWith(expectedRange);
  });

  it('should call onChange with next week when clicking on the next arrow', async () => {
    const onChange = jest.fn();
    const value = {
      startOfWeek: startOfDay(new Date('2021-07-12')),
      endOfWeek: endOfDay(new Date('2021-07-18')),
    };
    const expectedRange = {
      startOfWeek: startOfDay(new Date('2021-07-19')),
      endOfWeek: endOfDay(new Date('2021-07-25')),
    };
    const { driver: component } = render({ value, onChange });

    await component.clickOnNextWeekButton();

    expect(onChange).toHaveBeenLastCalledWith(expectedRange);
  });

  it('should have caption text that is MONTH YEAR when the dates fall within the same month', async () => {
    const expectedText = 'July 2021';
    const value = {
      startOfWeek: startOfDay(new Date('2021-07-19')),
      endOfWeek: endOfDay(new Date('2021-07-25')),
    };
    const { driver: component } = render({ value });

    const captionText = await component.getCaptionText();

    expect(captionText).toEqual(expectedText);
  });

  it('should have caption text that is MONTH - MONTH YEAR when the dates span two months', async () => {
    const expectedText = 'Dec - Jan 2022';
    const value = {
      startOfWeek: startOfDay(new Date('2021-12-26')),
      endOfWeek: endOfDay(new Date('2022-01-01')),
    };
    const { driver: component } = render({ value });

    const captionText = await component.getCaptionText();

    expect(captionText).toEqual(expectedText);
  });

  describe('a11y', () => {
    const value = {
      startOfWeek: startOfDay(new Date('2021-12-26')),
      endOfWeek: endOfDay(new Date('2022-01-01')),
    };

    const getArrowButton = (component, dataHook) =>
      component.find(`[data-hook="${dataHook}"]`);

    it('should have aria-label to next arrow button', async () => {
      const expectedAriaLabel = 'next aria-label';
      const component = enzymeRender(
        <WeeklyDateNavigation
          value={value}
          onChange={() => {}}
          nextArrowAriaLabel={expectedAriaLabel}
        />,
      );

      const ariaLabel = getArrowButton(component, DATA_HOOKS.Next).attr(
        'aria-label',
      );

      expect(ariaLabel).toEqual(expectedAriaLabel);
    });

    it('should have aria-label to prev arrow button', async () => {
      const expectedAriaLabel = 'prev aria-label';
      const component = enzymeRender(
        <WeeklyDateNavigation
          value={value}
          onChange={() => {}}
          prevArrowAriaLabel={expectedAriaLabel}
        />,
      );

      const ariaLabel = getArrowButton(component, DATA_HOOKS.Prev).attr(
        'aria-label',
      );

      expect(ariaLabel).toEqual(expectedAriaLabel);
    });

    it('should have aria-labelledby to next arrow button', async () => {
      const expectedAriaLabelledBy = 'next aria-labelledby';
      const component = enzymeRender(
        <WeeklyDateNavigation
          value={value}
          onChange={() => {}}
          nextArrowAriaLabelledBy={expectedAriaLabelledBy}
        />,
      );

      const ariaLabelledBy = getArrowButton(component, DATA_HOOKS.Next).attr(
        'aria-labelledby',
      );

      expect(ariaLabelledBy).toEqual(expectedAriaLabelledBy);
    });

    it('should have aria-labelledby to prev arrow button', async () => {
      const expectedAriaLabelledBy = 'prev aria-labelledby';
      const component = enzymeRender(
        <WeeklyDateNavigation
          value={value}
          onChange={() => {}}
          prevArrowAriaLabelledBy={expectedAriaLabelledBy}
        />,
      );

      const ariaLabelledBy = getArrowButton(component, DATA_HOOKS.Prev).attr(
        'aria-labelledby',
      );

      expect(ariaLabelledBy).toEqual(expectedAriaLabelledBy);
    });
  });
});
